import { IAvatarModels } from 'types/avatar'
import { IMediaItem } from 'types/media'
import { AIVideoSocketMessageStatus } from 'types/aiVideo'

export enum AvatarSource {
  UPLOAD = 'upload',
  MY_FILES = 'myFiles',
  PUBLIC = 'public'
}

export interface IAvatarData {
  id: string
  url: string
  source: AvatarSource
  // Natural pixel size of the source image, when known (uploaded / My-Files
  // avatars). Used to FIT the loading placeholder to the photo's own aspect ratio
  // so the loader doesn't stretch it. Absent for public avatars (no pixel size —
  // those fall back to the selected aspect ratio).
  width?: number
  height?: number
}

export interface IPublicAvatar {
  id: number
  order: number
  url_16_9: string
  url_1_1: string
  url_9_16: string
  thumbnail_16_9: string
  thumbnail_1_1: string
  thumbnail_9_16: string
  gender: string
}

export interface IPublicAvatarsQuery {
  data: IPublicAvatar[] | null
  isLoading: boolean
}

// Avatar generation models. We only keep `id` + `possibleQualities` from the
// API response since that's all the quality picker needs.
export interface IAvatarModelsQuery {
  data: IAvatarModels[] | null
  isLoading: boolean
}

// Free-trial "gift" eligibility for the current (free) user. `isAccessible`
// drives the generate footer variant; `accessibleDuration` is the free length
// (seconds) the gift grants — stored for display/future use.
export interface IAvatarGiftAccess {
  isAccessible: boolean
  accessibleDuration: number
}

export interface IAvatarGiftAccessQuery {
  data: IAvatarGiftAccess | null
  isLoading: boolean
}

// The selected voice for the avatar (catalog of languages/speakers/tones lives
// in the shared `voiceCatalog` slice; only the selection is stored here).
export interface IAvatarVoice {
  langCode: string
  speakerId: number
  speed: number
}

// Which sub-tab of the Voices section is active.
export enum AvatarVoiceTab {
  LIBRARY = 'library',
  CLONE = 'clone'
}

export type TVoiceCloneUploadStatus = 'idle' | 'uploading' | 'failed'

// A voice file attached to the avatar for voice cloning. Removing it from the
// avatar (the X on the card) only detaches it — the file stays in
// My Files → Sounds → Voice Clone.
export interface IClonedVoice {
  // My Files media id (sound id from the upload response / media library item).
  // For a clone restored onto a regeneration form it's the project's
  // reference-audio record id instead — the sound id isn't in that response.
  id: number
  name: string
  url: string
  source: AvatarSource.UPLOAD | AvatarSource.MY_FILES
}

export interface IVoiceCloneState {
  activeTab: AvatarVoiceTab
  voice: IClonedVoice | null
  uploadStatus: TVoiceCloneUploadStatus
}

// The avatar data that gets posted / read as the current avatar.
export interface IAvatarGenerationParams {
  avatar: IAvatarData | null
  voice: IAvatarVoice
  quality: number
  speechText: string
  // The avatar's aspect ratio (e.g. '16:9' | '9:16'), independent of the
  // project. `null` = follow the project's current aspect ratio (the default).
  aspectRatio: string | null
  // Timeline item id of the clip this form regenerates, or null for a fresh
  // generation. Marks the form as a regeneration form.
  targetMediaItemId: string | null
}

export interface IAvatarGenerationResponse {
  aiAvatarVideoHash: string
  aiAvatarProjectId: number
}

// Response of GET /gen-ai/avatar/{avatarProjectId}. On completion `mediaItems`
// carries the rendered samples as full media items — the same shape the socket
// FINISHED event delivers — so a load-time reconcile can splice them in.
export interface IAvatarProject {
  id: number
  userId: number
  text: string
  speakerId: number
  aspectRatio: string
  imageUrl: string
  source: string | null
  mediaItems: IMediaItem[]
  videoModel: string
  projectId: number
  status: AIVideoSocketMessageStatus | null
  createdAt?: string
  updatedAt?: string
}

export interface IAvatarGenerationRequestBody {
  videoModel?: string
  quality: number
  text: string
  aspectRatio: string
  imageUrl: string
  // speakerId is omitted when refAudio is sent — the backend expects one or
  // the other, never both (see `activeRefAudio` selector).
  speakerId?: number
  // Uploaded voice-clone audio URL; replaces the catalog speaker.
  refAudio?: string
  source: string
  sourceProjectId: number
}

export interface IAiAvatarState {
  isLoadingTabData: boolean
  generationParams: IAvatarGenerationParams
  isAvatarLoading: boolean
  // Voice clone state. Kept outside generationParams because the attached
  // clone only enters the payload while the Voice clone sub-tab is active
  // (`activeRefAudio` selector) — attachment alone isn't a generation param.
  // Living outside the params means the form flows don't carry it, so the
  // `setGenerationParams`/`resetGenerationParams` reducers reset it explicitly
  // — otherwise a stale clone would override a freshly loaded `speakerId`.
  voiceClone: IVoiceCloneState
  // Clones the generations of THIS session were kicked off with, keyed by each
  // generation's `aiAvatarProjectId` (every rendered sample carries it as
  // `meta.aiAvatarProjectId`). The regen form / history prefill re-attaches the
  // clone from here after the `setGenerationParams` voice-clone reset, so a
  // clone-generated clip regenerates with its `refAudio` — without relying on
  // the backend echoing `refAudio` back in the projects response. Session-only:
  // survives form resets (lives outside `generationParams`/`voiceClone`) but
  // not a reload.
  generationClones: Record<number, IClonedVoice>
  publicAvatars: IPublicAvatarsQuery
  avatarModels: IAvatarModelsQuery
  giftAccess: IAvatarGiftAccessQuery
  isGenerating: boolean
  // A free user kicked off a generation and we're waiting for the mid-render
  // `voiceover_generated` socket event to re-check gift access (the voiceover
  // step is when the gift/credit is actually consumed). True from kickoff until
  // that re-check resolves; holds the Generate footer in a loading state so the
  // user can't act on the now-stale free-trial CTA. Free users only.
  isAwaitingGiftRecheck: boolean
}
